import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from '../../../redux/features/appData';
import {
  EmployeeRecord,
  EmployeeReferenceType,
  Task,
  TaskStatus,
  Workflow,
} from 'winslow-core';
import { Button, Icon } from '../../../ds2/components';
import { 
  X, 
  User, 
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  OctagonX,
  Workflow as WorkflowIcon,
  RefreshCw,
} from 'lucide-react';
import { apiGetWorkflow } from '../../../services/WorkflowService';
import { apiGetTasksForWorkflow } from '../../../services/TaskService';
import { apiGetEmployeeRecordsForWorkspace } from '../../../services/EmployeeRecordService';
import { erGetCPValue } from '../app-employee-records/employeeRecordUtils';
import WorkflowDiagram from './WorkflowDiagram';
import ModalBasicConfirm from '../../common/ModalBasicConfirm';
import { toastFail, toastSuccess } from '../../../config';
import toast from 'react-hot-toast';
import './AppWorkflow.css';
import { openEmployeeProfileSidebar } from '../../../redux/features/agent/agentSlice';

type AppWorkflowProps = {
  dynamicContext: {
    workflowId: string;
  };
  disabled: boolean;
}

export const AppWorkflow = ({ dynamicContext, disabled }: AppWorkflowProps) => {

  const dispatch = useDispatch();
  
  const storeUser = useSelector(selectUser);
  
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [employeeRecords, setEmployeeRecords] = useState<EmployeeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWorkflowDiagram, setShowWorkflowDiagram] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  useEffect(() => {
    if (dynamicContext?.workflowId) {
      loadWorkflowData(dynamicContext.workflowId);
    }
  }, [dynamicContext?.workflowId]);

  const loadWorkflowData = async (workflowId: string) => {
    try {
      setLoading(true);
      setError(null);

      const [workflowData, tasksData, employeeRecordsData] = await Promise.all([
        apiGetWorkflow(workflowId!),
        apiGetTasksForWorkflow(workflowId!),
        apiGetEmployeeRecordsForWorkspace(storeUser?.currentWorkspaceId || "")
      ]);

      setWorkflow(workflowData);
      setTasks(tasksData || []);
      setEmployeeRecords(employeeRecordsData || []);
      
      console.log('[AppWorkflow] Employee records loaded:', employeeRecordsData);
      console.log('[AppWorkflow] Tasks loaded:', tasksData);
    } catch (err) {
      console.error('Error loading workflow data:', err);
      setError('Failed to load workflow data');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelWorkflow = async () => {
    if (!workflow) return;

    try {
      // TODO: Implement cancel workflow API call
      console.log('Cancelling workflow:', workflow.id);
      
      // For now, just show a success message
      toast('Workflow cancelled successfully', toastSuccess);
      setShowCancelConfirmation(false);
      
      // Reload data to reflect changes
      await loadWorkflowData(workflow.id);
    } catch (err) {
      console.error('Error cancelling workflow:', err);
      toast('Failed to cancel workflow', toastFail);
    }
  };

  const getTaskStatusIcon = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.WAITING:
      case TaskStatus.ACTIVE:
        return <Icon icon={<Clock size={16} color="#f59e0b" />} size="sm" />;
      case TaskStatus.COMPLETE:
        return <Icon icon={<CheckCircle size={16} color="#10b981" />} size="sm" />;
      case TaskStatus.REJECTED:
        return <Icon icon={<XCircle size={16} color="#ef4444" />} size="sm" />;
      default:
        return <Icon icon={<AlertCircle size={16} color="#6b7280" />} size="sm" />;
    }
  };

  const getTaskStatusText = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.WAITING:
        return 'Waiting';
      case TaskStatus.ACTIVE:
        return 'Active';
      case TaskStatus.COMPLETE:
        return 'Complete';
      case TaskStatus.REJECTED:
        return 'Rejected';
      default:
        return 'Unknown';
    }
  };

  const getTaskStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.WAITING:
      case TaskStatus.ACTIVE:
        return '#f59e0b';
      case TaskStatus.COMPLETE:
        return '#10b981';
      case TaskStatus.REJECTED:
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getEmployeeName = (employeeRecordId: string): string => {
    const employeeRecord = employeeRecords.find(er => er.id === employeeRecordId);
    console.log('[AppWorkflow] Looking for employee record:', employeeRecordId, 'Found:', employeeRecord);
    
    if (!employeeRecord) return 'Unknown';

    const firstName = erGetCPValue(employeeRecord, "system-firstName");
    const lastName = erGetCPValue(employeeRecord, "system-lastName");
    console.log('[AppWorkflow] First name:', firstName, 'Last name:', lastName);

    return `${firstName} ${lastName}`;
  };

  const getEmployeeAvatar = (employeeRecordId: string): string | null => {
    const employeeRecord = employeeRecords.find(er => er.id === employeeRecordId);
    return employeeRecord?.avatarURL || null;
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} sec${diffInSeconds !== 1 ? 's' : ''}`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} min${diffInMinutes !== 1 ? 's' : ''}`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''}`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''}`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return `${diffInWeeks} week${diffInWeeks !== 1 ? 's' : ''}`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''}`;
    }

    const diffInYears = Math.floor(diffInDays / 365);
    return `${diffInYears} year${diffInYears !== 1 ? 's' : ''}`;
  };

  // Sort tasks by creation date, newest first
  const sortedTasks = [...tasks].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (loading) {
    return (
      <div className="app-workflow-container">
        <div className="app-workflow-loading">
          <p>Loading workflow...</p>
        </div>
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className="app-workflow-container">
        <div className="app-workflow-error">
          <AlertCircle size={24} />
          <p>{error || 'Workflow not found'}</p>
          <Button
            variant="primary"
            size="sm"
            label="Retry"
            onClick={() => loadWorkflowData(dynamicContext.workflowId)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="app-workflow-container">
      {/* Header */}
      <div className="app-workflow-header dc-component-header">
        <div className="app-workflow-title">
            {workflow.vibeProcess?.name || 'Unknown Process'}
        </div>
        
        <div className="app-workflow-actions">
          <Icon
            variant="tertiary"
            size="md"
            icon={<RefreshCw size={16} />}
            border={false}
            // label="Visualize"
            onClick={() => loadWorkflowData(dynamicContext.workflowId)}
          />
          <Icon
            variant="tertiary"
            size="md"
            icon={<WorkflowIcon size={16} />}
            border={false}
            // label="Visualize"
            onClick={() => setShowWorkflowDiagram(true)}
          />
          <Icon
            variant="tertiary"
            size="md"
            icon={<OctagonX size={16} color="red"/>}
            border={false}
            // label="Cancel"
            onClick={() => setShowCancelConfirmation(true)}
          />
        </div>
      </div>

      {/* Tasks Section */}
      <div className="app-workflow-tasks">
        {/* <div className="app-workflow-tasks-header">
          <h2>Tasks ({tasks.length})</h2>
          <p className="app-workflow-tasks-subtitle">
            Tasks for this workflow, sorted by newest first
          </p>
        </div> */}

        {tasks.length === 0 ? (
          <div className="app-workflow-empty">
            <WorkflowIcon size={48} />
            <h3>No tasks found</h3>
            <p>This workflow doesn't have any tasks yet.</p>
          </div>
        ) : (
          <div className="app-workflow-tasks-table-container">
            <table className="app-workflow-tasks-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {sortedTasks.map((task) => (
                  <tr key={task.id} className="app-workflow-task-row">
                    <td className="app-workflow-task-cell">
                      <div className="app-workflow-task-info">
                        {task.name}
                        {/* {task.description && (
                          <p className="app-workflow-task-description">{task.description}</p>
                        )} */}
                      </div>
                    </td>
                    <td className="app-workflow-task-cell">
                      <div 
                        className="app-workflow-task-owner"
                        onClick={() => {
                          dispatch(openEmployeeProfileSidebar({
                            employeeId: task.ownerEmployeeRecordId,
                            type: EmployeeReferenceType.EMPLOYEE_RECORD
                          }));
                        }}
                      >
                        {getEmployeeAvatar(task.ownerEmployeeRecordId) ? (
                          <img 
                            src={getEmployeeAvatar(task.ownerEmployeeRecordId)!} 
                            alt="Owner avatar"
                            className="app-workflow-task-owner-avatar"
                          />
                        ) : (
                          <User size={14} />
                        )}
                        <span>{getEmployeeName(task.ownerEmployeeRecordId)}</span>
                      </div>
                    </td>
                    <td className="app-workflow-task-cell">
                      <div className="app-workflow-task-status">
                        {getTaskStatusIcon(task.status)}
                        <span
                          className="app-workflow-task-status-text"
                          style={{ color: getTaskStatusColor(task.status) }}
                        >
                          {getTaskStatusText(task.status)}
                        </span>
                      </div>
                    </td>
                    <td className="app-workflow-task-cell">
                      <div className="app-workflow-task-type">
                        {task.isApproval ? (
                          <div className="app-workflow-task-type-badge app-workflow-task-type-approval">
                            <User size={14} />
                            Approval
                          </div>
                        ) : (
                          <div className="app-workflow-task-type-badge app-workflow-task-type-task">
                            <WorkflowIcon size={14} />
                            Task
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="app-workflow-task-cell">
                      <div className="app-workflow-task-created">
                        {/* <Calendar size={14} /> */}
                        <span>{formatTimeAgo(new Date(task.createdAt))}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Workflow Diagram Modal */}
      {showWorkflowDiagram && (
        <div className="app-workflow-diagram-modal">
          <div className="app-workflow-diagram-content">
            <div className="app-workflow-diagram-header">
              <h3>Workflow Diagram</h3>
              <Button
                variant="tertiary"
                size="sm"
                leftIcon={<X size={16} />}
                label="Close"
                onClick={() => setShowWorkflowDiagram(false)}
              />
            </div>
            <div className="app-workflow-diagram-body">
              {workflow.vibeProcess?.workflowDiagram ? (
                <WorkflowDiagram workflowData={workflow.vibeProcess.workflowDiagram} />
              ) : (
                <p>No workflow diagram available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirmation && (
        <ModalBasicConfirm
          isOpen={showCancelConfirmation}
          title="Cancel Workflow"
          message="Are you sure you want to cancel this workflow? This action cannot be undone."
          continueLabel="Cancel Workflow"
          onContinue={handleCancelWorkflow}
          onClose={() => setShowCancelConfirmation(false)}
        />
      )}
    </div>
  );
};
